import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { classNames } from '../../utils/cssUtils'
import './ContextMenu.scss'

interface Props {
	children: ReactNode | ReactNode[]
	alignment?: 'left' | 'right' | 'center'
	onMouseLeave?: () => void
	id?: string
}

/**
 * ContextMenu that shows itself whennever the parent element is hovered
 * and closes whenever the mouse leaves the bounds defined by the rect
 * that surrounds both the parent and itself.
 */
export default ({ id, children, alignment, onMouseLeave }: Props) => {
	const [showSelf, setShowSelf] = useState(false)

	const ref = useRef<HTMLDivElement>(null)
	const cs = classNames('context-menu mvp-box', {
		'is-open': showSelf,
		'is-aligned-left': alignment === 'left',
		'is-aligned-right': alignment === 'right',
		'is-aligned-center': alignment === 'center',
	})

	// Add mouse enter listener on parent to trigger show
	useEffect(() => {
		const parent = ref.current?.parentElement
		if (!parent) {
			return
		}

		const mouseEnterHandler = function (this: HTMLElement, event: MouseEvent) {
			setShowSelf(true)
		}

		parent.addEventListener('mouseenter', mouseEnterHandler)

		return () => {
			if (parent) {
				parent.removeEventListener('mouseenter', mouseEnterHandler)
			}
		}
	}, [])

	// Add mouse listener to check if mouse has left parent
	useEffect(() => {
		const handleMouseMove = function (this: Document, event: MouseEvent) {
			const parent = ref.current?.parentElement
			const menu = ref.current
			if (!menu || !parent) {
				return
			}
			const parentBounds = parent.getBoundingClientRect()
			const menuBounds = menu.getBoundingClientRect()

			const aboveButton = event.clientY < parentBounds.y
			const belowMenu = event.clientY > menuBounds.y + menuBounds.height
			const leftOfMinX = event.clientX < Math.min(parentBounds.x, menuBounds.x)
			const rightOfMaxX = event.clientX > Math.max(parentBounds.x + parentBounds.width, menuBounds.x + menuBounds.width)
			const shouldClose = aboveButton || belowMenu || leftOfMinX || rightOfMaxX

			if (shouldClose) {
				setShowSelf(false)
			}
		}

		if (showSelf) {
			document.addEventListener('mousemove', handleMouseMove)
		}

		return () => {
			if (showSelf) {
				document.removeEventListener('mousemove', handleMouseMove)
			}
		}
	}, [showSelf])

	return (
		<div id={id} ref={ref} className={cs} onMouseLeave={onMouseLeave}>
			{children}
		</div>
	)
}
